import React, { useEffect, useRef, useState } from 'react'

import PropTypes from 'prop-types'
import { FormContext, useForm } from 'react-hook-form'
import { Box, Button, Collapse, Divider, Paper } from '@material-ui/core'

import { ConfirmationDialog } from 'components'
import {
  EvidenceSection,
  PlanActionFields,
  PlanActionFormHeader,
} from './components'

import useSnackbar from 'hooks/useSnackbar'
import useThirdPartyManagementStepper from 'hooks/useThirdPartyManagementStepper'

import helpers from 'helpers'
import * as service from 'service'
import schema from './schema'

const PlanActionForm = ({
  evidencePlanAction = null,
  users = [],
  isUsersLoading = false,
  setEvidencePlanActions,
}) => {
  const [loading, setLoading] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const planAction = evidencePlanAction?.planActions?.[0]
  const isNew = !planAction?.id

  const [openCollapse, setOpenCollapse] = useState(isNew)

  const snackbar = useSnackbar()
  const { isShow, companySupplierId } = useThirdPartyManagementStepper()
  const containerRef = useRef(null)

  const formMethods = useForm({
    validationSchema: schema,
    defaultValues: {
      what: planAction?.what ?? '',
      how: planAction?.how ?? '',
      because: planAction?.because ?? '',
      where: planAction?.where ?? '',
      howPrice: planAction?.howPrice ?? '',
      link: planAction?.link ?? '',
      startDate: planAction?.startDate ? new Date(planAction?.startDate) : null,
      deadlineDate: planAction?.deadlineDate
        ? new Date(planAction?.deadlineDate)
        : null,
      responsibleUserId: planAction?.responsible?.id ?? '',
      justify: evidencePlanAction?.evidence?.justify ?? '',
      evidences: evidencePlanAction?.evidence?.attachments,
    },
  })

  const { handleSubmit } = formMethods

  const handleClickDelete = () => {
    if (isNew)
      return setEvidencePlanActions((current) =>
        current.filter(
          (item) => item.draftKey !== evidencePlanAction?.draftKey,
        ),
      )

    setOpenDeleteDialog(true)
  }

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true)

      await service.dponet.suppliers.evidencePlanActions.destroy({
        companySupplierId,
        evidencePlanActionId: evidencePlanAction?.id,
      })

      snackbar.open({
        message: 'Plano de ação excluído com sucesso!',
        variant: 'success',
      })

      setEvidencePlanActions((current) =>
        current.filter((item) => item.id !== evidencePlanAction?.id),
      )
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao excluir plano de ação',
        variant: 'error',
      })
    } finally {
      setDeleting(false)
    }
  }

  const onSubmit = async (data) => {
    try {
      setLoading(true)

      const { startDate, deadlineDate, justify, evidences, ...rest } = data

      const evidence = {
        justify,
        attachments: helpers.file.toArray(evidences),
      }

      const planActions = [
        {
          ...rest,
          ...(planAction?.id && { id: planAction.id }),
          startDate: startDate?.toISOString(),
          deadlineDate: deadlineDate?.toISOString(),
        },
      ]

      let response

      if (isNew) {
        response = await service.dponet.suppliers.evidencePlanActions.create({
          companySupplierId,
          evidence,
          planActions,
        })
      } else {
        response = await service.dponet.suppliers.evidencePlanActions.update({
          companySupplierId,
          evidencePlanActionId: evidencePlanAction?.id,
          evidence,
          planActions,
        })
      }

      snackbar.open({
        message: `Plano de ação ${
          isNew ? 'criado' : 'atualizado'
        } com sucesso!`,
        variant: 'success',
      })

      const savedEvidencePlanAction = response?.data?.evidencePlanAction

      setEvidencePlanActions((current) =>
        current.map((item) => {
          const matchesDraft =
            evidencePlanAction?.draftKey != null &&
            item.draftKey === evidencePlanAction.draftKey
          const matchesSaved =
            evidencePlanAction?.id != null && item.id === evidencePlanAction.id

          return matchesDraft || matchesSaved ? savedEvidencePlanAction : item
        }),
      )
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao salvar plano de ação',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isNew) return
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Paper variant="outlined" ref={containerRef}>
        <PlanActionFormHeader
          id={evidencePlanAction?.id}
          isDraft={isNew}
          openCollapse={openCollapse}
          setOpenCollapse={setOpenCollapse}
          onDelete={handleClickDelete}
        />

        <FormContext {...formMethods}>
          <Collapse in={openCollapse} timeout="auto" unmountOnExit>
            <Divider />
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              display="flex"
              flexDirection="column"
              gridGap={24}
              px={3}
              py={2}
            >
              <PlanActionFields users={users} isUsersLoading={isUsersLoading} />

              <EvidenceSection
                attachments={evidencePlanAction?.evidence?.attachments}
              />

              {!isShow && (
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                  >
                    Salvar
                  </Button>
                </Box>
              )}
            </Box>
          </Collapse>
        </FormContext>
      </Paper>

      {openDeleteDialog && (
        <ConfirmationDialog
          open={openDeleteDialog}
          setOpen={setOpenDeleteDialog}
          text="Tem certeza que deseja excluir este plano de ação?"
          textButton="Excluir"
          actionAcceptButton={handleConfirmDelete}
          loading={deleting}
        />
      )}
    </>
  )
}

PlanActionForm.propTypes = {
  evidencePlanAction: PropTypes.object,
  users: PropTypes.array,
  isUsersLoading: PropTypes.bool,
  setEvidencePlanActions: PropTypes.func.isRequired,
}

export default PlanActionForm
